<script lang="ts" module>
  import { uniqueContext } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"
  import type { Align, Side } from "."

  type Boundary = Element | null

  export type PopperContentProps = BaseProps & {
    side?: Side
    sideOffset?: number
    align?: Align
    alignOffset?: number
    arrowPadding?: number
    width?: string
    minWidth?: string
    collisionBoundary?: Boundary | Boundary[]
    collisionPadding?: number | Partial<Record<Side, number>>
    sticky?: "partial" | "always"
    hideWhenDetached?: boolean
    avoidCollisions?: boolean
    ref?: HTMLElement | null
    children?: any
  }

  export type ContentState = {
    placedSide: Side
    arrowX: number
    arrowY: number
    shouldHideArrow: boolean
    arrowWidth: number
    arrowHeight: number
  }

  const { getContext, setContext } = uniqueContext<ContentState>()
  export const getPopperContentContext = getContext
</script>

<script lang="ts">
  import { useActions } from "#lib/internal/helpers"
  import {
    computePosition,
    flip,
    hide,
    limitShift,
    offset,
    shift,
    size,
    type ComputePositionConfig,
    type MiddlewareData,
    type Placement,
    type Strategy,
  } from "@floating-ui/core"
  import {
    autoUpdate,
    arrow as floatingUIarrow,
    platform,
  } from "@floating-ui/dom"
  import { onDestroy, type Snippet } from "svelte"
  import { getPopperRootContext } from "./PopperRoot.svelte"
  import {
    getSideAndAlignFromPlacement,
    isDefined,
    isNotNull,
    transformOrigin,
  } from "./utils"

  type ContentProps = PopperContentProps & { children?: Snippet }

  let {
    use = [],
    ref = $bindable(null),
    side = "bottom" as Side,
    sideOffset = 0,
    align = "center",
    alignOffset = 0,
    arrowPadding = 0,
    collisionBoundary = [],
    collisionPadding = 0,
    sticky = "partial",
    hideWhenDetached = false,
    avoidCollisions = true,
    width = undefined,
    minWidth = undefined,
    children,
    ...restProps
  }: ContentProps = $props()

  const rootCtx = getPopperRootContext()

  let ctxPlacedSide: Side = $state("bottom")
  let ctxArrowX: number = $state(0)
  let ctxArrowY: number = $state(0)
  let ctxShouldHideArrow: boolean = $state(false)
  let ctxArrowWidth: number = $state(0)
  let ctxArrowHeight: number = $state(0)

  setContext({
    get placedSide() {
      return ctxPlacedSide
    },
    get arrowX() {
      return ctxArrowX
    },
    get arrowY() {
      return ctxArrowY
    },
    get shouldHideArrow() {
      return ctxShouldHideArrow
    },
    get arrowWidth() {
      return ctxArrowWidth
    },
    set arrowWidth(v) {
      ctxArrowWidth = v
    },
    get arrowHeight() {
      return ctxArrowHeight
    },
    set arrowHeight(v) {
      ctxArrowHeight = v
    },
  })

  let content: HTMLElement = $state()!

  $effect(() => {
    if (content) ref = content
  })

  onDestroy(() => {
    ref = null
  })

  const desiredPlacement = $derived(
    (side + (align !== "center" ? "-" + align : "")) as Placement
  )

  const collisionPaddingNormalized = $derived(
    typeof collisionPadding === "number"
      ? collisionPadding
      : { top: 0, right: 0, bottom: 0, left: 0, ...collisionPadding }
  )

  const boundary = $derived(
    Array.isArray(collisionBoundary) ? collisionBoundary : [collisionBoundary]
  )
  const hasExplicitBoundaries = $derived(boundary.length > 0)

  const detectOverflowOptions = $derived({
    padding: collisionPaddingNormalized,
    boundary: boundary.filter(isNotNull),
    altBoundary: hasExplicitBoundaries,
  })

  let strategy: Strategy | null = $state(null)
  let placement: Placement | null = $state(null)
  let middlewareData: MiddlewareData | null = $state(null)
  let x: number | null = $state(null)
  let y: number | null = $state(null)

  function roundByDPR(value: number) {
    const dpr = window.devicePixelRatio || 1
    return Math.round(value * dpr) / dpr
  }

  function updatePosition(anchor: HTMLElement, options: ComputePositionConfig) {
    computePosition(anchor, content, options).then(async (position) => {
      ;({ strategy, placement, middlewareData, x, y } = position)

      const resolvedSide = position.placement
        ? getSideAndAlignFromPlacement(position.placement)[0]
        : side

      ctxPlacedSide = resolvedSide
      ctxArrowX = position.middlewareData?.arrow?.x || 0
      ctxArrowY = position.middlewareData?.arrow?.y || 0
      ctxShouldHideArrow = position.middlewareData?.arrow?.centerOffset !== 0
    })
  }

  let cleanup: (() => void) | null = null

  onDestroy(() => {
    cleanup?.()
  })

  $effect(() => {
    const anchor = rootCtx.anchor
    if (content && anchor && !cleanup) {
      cleanup = autoUpdate(anchor, content, () => {
        const arrowWidth = ctxArrowWidth
        const arrowHeight = ctxArrowHeight
        const arrow = rootCtx.arrow

        updatePosition(anchor, {
          platform,
          strategy: "absolute",
          placement: desiredPlacement,
          middleware: [
            offset({
              mainAxis: sideOffset + arrowHeight,
              alignmentAxis: alignOffset,
            }),
            avoidCollisions
              ? shift({
                  mainAxis: true,
                  crossAxis: false,
                  limiter: sticky === "partial" ? limitShift() : undefined,
                  ...detectOverflowOptions,
                })
              : undefined,
            avoidCollisions ? flip({ ...detectOverflowOptions }) : undefined,
            size({
              ...detectOverflowOptions,
              apply: ({ elements, rects, availableWidth, availableHeight }) => {
                const { width: anchorWidth, height: anchorHeight } =
                  rects.reference
                const contentStyle = elements.floating.style
                const safeSet = (prop: string, val: string) => {
                  try {
                    contentStyle.setProperty(prop, val)
                  } catch {
                    // JSDOM may reject non-string values during testing
                  }
                }
                safeSet("--popper-available-width", `${availableWidth}px`)
                safeSet("--popper-available-height", `${availableHeight}px`)
                safeSet("--popper-anchor-width", width || `${anchorWidth}px`)
                safeSet(
                  "--popper-anchor-min-width",
                  minWidth || width || `${anchorWidth}px`
                )
                safeSet("--popper-anchor-height", `${anchorHeight}px`)
              },
            }),
            arrow
              ? floatingUIarrow({
                  element: arrow,
                  padding: arrowPadding,
                })
              : undefined,
            transformOrigin({ arrowWidth, arrowHeight }),
            hideWhenDetached
              ? hide({ strategy: "referenceHidden" })
              : undefined,
          ].filter(isDefined),
        })
      })
    }
  })

  const isPlaced = $derived(x !== null && y !== null)

  const placedSideAndAlign = $derived(
    placement
      ? getSideAndAlignFromPlacement(placement)
      : ([side, undefined] as [Side, string | undefined])
  )
  const placedSide = $derived(placedSideAndAlign[0])
  const placedAlign = $derived(placedSideAndAlign[1])

  const contentZIndex = $derived(
    (content && window?.getComputedStyle(content).zIndex) || 0
  )

  $effect(() => {
    if (rootCtx.anchor) rootCtx.anchor.dataset.side = placedSide
  })
</script>

<div
  bind:this={content}
  data-radix-popper-content-wrapper=""
  style:position={strategy}
  style:left={0}
  style:top={0}
  style:transform={isPlaced
    ? `translate3d(${roundByDPR(x ?? 0)}px, ${roundByDPR(y ?? 0)}px, 0)`
    : "translate3d(0, -200%, 0)"}
  style:min-width="var(--popper-anchor-min-width)"
  style:z-index={contentZIndex}
  style:--radix-popper-transform-origin={middlewareData?.transformOrigin?.x +
    " " +
    middlewareData?.transformOrigin?.y}
  dir={restProps.dir}
  use:useActions={use ?? []}
>
  <div
    {...restProps}
    data-side={placedSide}
    data-align={placedAlign}
    style:animation="{isPlaced ? undefined : 'none'},"
    style:opacity={middlewareData?.hide?.referenceHidden ? 0 : undefined}
  >
    {@render children?.()}
  </div>
</div>
